import { useState, useEffect, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';

import {
  EntitlementSlugEnum,
  ProductTypeName,
  SortOrder,
} from '@electricjs/core_entity-client';
import { Box, Flex, spaces, useBreakpoint } from '@electricjs/arc';

import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';
import { PRODUCT_TYPES_BY_TYPE_GROUP } from '@/constants/storefront';
import { STOREFRONT_PAGE_SIZE } from '@/constants/pagination';
import { SortingParams } from '@/types/common';
import { SelectionParamKeys } from '@/types/queryParamKeys';
import {
  ProductOriginName,
  useLazyGetPaginatedProductsQuery,
  useLazyGetProductQuery,
} from '@/redux/slices/productApiSlice';

import { useStorefront } from '../../StorefrontProvider';

import {
  DynamicFilters,
  StoreFiltersSelections,
  Store,
  StorefrontProductTypeGroup,
} from '@/types/storefront';

import { useItemDetailsModal } from '../useItemDetailsModal';
import { DynamicFiltersGroup } from '../Filters/DynamicFiltersGroup';
import {
  ItemList,
  ProductTypeGroupSelector,
  ProductTypeGroupSelectorProps,
} from '..';
import { mergeDynamicFiltersFromProductTypes } from '../../utils';
import SortingSelector from '../Filters/SortingSelector';

import { BuyItAgainSuggestionBox } from '../BuyItAgain/BuyItAgainSuggestionBox';
import { getShoppingCountryOptions } from '../ShoppingCountrySelector';

export type CatalogStoreProps = Omit<
  ProductTypeGroupSelectorProps,
  'onProductTypesSelectFromGroup'
>;

export const CatalogStore = ({
  selectedProductTypeGroup,
  onProductTypeGroupSelect,
  laptopRecommendationUrl,
}: CatalogStoreProps) => {
  const { t } = useTranslation('common');

  const { md } = useBreakpoint();

  const {
    catalogCart,
    storefrontCheckoutForm,
    addCatalogCartEntry,
    removeCatalogCartEntry,
  } = useStorefront();
  const { storefrontBuyItAgain } = useFlags();

  const orgHasMdmEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.JumpcloudMdm
  );

  //  Check if an employee is selected
  const selectedEmployee = storefrontCheckoutForm?.getValues('employee');
  const isEmployeeSelected = Boolean(selectedEmployee?.value);

  // Selected shopping country (country or "other")
  const selectedShoppingCountry =
    storefrontCheckoutForm?.watch('shoppingCountry') ??
    getShoppingCountryOptions()[0];

  // Just for typescript, because this component will never be rendered in a use-case that the shopping country is "other"
  if (selectedShoppingCountry.value === 'other') {
    throw new Error();
  }

  // whether needs to search for laptops with Win ZTP available
  const needsWindowsZtpAvailable =
    orgHasMdmEntitlement && // products are filtered by Windows ZTP availability based on "org-level" MDN entitlement, but the enable/disable of Win ZTP is handled by employee-level entitlement
    selectedProductTypeGroup === StorefrontProductTypeGroup.Laptops &&
    selectedShoppingCountry.value === 'US';

  const specificProductOriginName =
    selectedShoppingCountry.value === 'CA'
      ? ProductOriginName.TDSynnexCanada
      : undefined;

  const [selectedProductTypes, setSelectedProductTypes] = useState<
    ProductTypeName[]
  >(PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup]); // initializing with the types from the selected group of types

  const [
    isAvailableDynamicFiltersLoading,
    setIsAvailableDynamicFiltersLoading,
  ] = useState(false);

  const [dynamicFiltersSelectedValues, setDynamicFiltersSelectedValues] =
    useState<DynamicFilters>();

  const [selectedSorting, setSelectedSorting] = useState<SortingParams>({
    orderBy: 'name',
    sortOrder: SortOrder.Asc,
  });

  const [selectedPage, setSelectedPage] = useState<number>(0);

  const [
    getPaginatedProducts,
    {
      data: productsResponse,
      isFetching: isProductListLoading,
      isError: hasProductListError,
    },
  ] = useLazyGetPaginatedProductsQuery();

  // Product list (the search results)
  const productList = productsResponse?.paginatedResponse.results || [];

  // Dynamic filters (for the search results).
  // The backend returns the dynamic filters per product type that exists in the search results.
  // We merge them into a single group of dynamic filters.
  const availableDynamicFilters = mergeDynamicFiltersFromProductTypes(
    productsResponse?.filters ?? {}
  );

  const applyFiltersSelections = useCallback(
    ({
      selectedProductTypes,
      dynamicFiltersSelectedValues,
      selectedSorting,
      selectedPage,
      needsWindowsZtpAvailable,
      countryCode,
      specificProductOriginName,
    }: StoreFiltersSelections) => {
      // fetch the products based on the selections
      getPaginatedProducts(
        {
          productTypes: selectedProductTypes,
          dynamicFilters: dynamicFiltersSelectedValues,
          orderBy: selectedSorting.orderBy,
          sortOrder: selectedSorting.sortOrder,
          offset: selectedPage * STOREFRONT_PAGE_SIZE,
          limit: STOREFRONT_PAGE_SIZE,
          windowsZtpAvailable: needsWindowsZtpAvailable,
          countryCode,
          productOriginName: specificProductOriginName,
        },
        true
      )
        .unwrap()
        .catch(error => console.error('Error while fetching products', error))
        .finally(() => setIsAvailableDynamicFiltersLoading(false));
    },
    [getPaginatedProducts]
  );

  /**
   * When product types are selected from a group selector (using the big buttons in the top).
   * For example: Computers, Monitors, Keyboards and Mice, Docks, etc.
   * The "Keyboards and Mice" group is, in fact, 3 ProductTypes selected at once (keyboard, mouse, combo).
   *
   * This selection is handled separately, because it resets all other filters and impacts dynamic filters loading.
   */
  const handleTypesSelectionFromGroup = useCallback(
    (types: ProductTypeName[]) => {
      setSelectedProductTypes([...types]);

      // reset all other filters, sorting, pagination
      setDynamicFiltersSelectedValues(undefined);

      setSelectedSorting({
        orderBy: 'name',
        sortOrder: SortOrder.Asc,
      });

      setSelectedPage(0);

      // When product type group is changed, the available dynamic filters will change, so we need to set them as "loading".
      // The value will be cleared as soon as the "getPaginatedProducts" ends.
      setIsAvailableDynamicFiltersLoading(true);
    },
    []
  );

  /**
   * When a user selects product types from a dynamic filter (not from groups of types).
   * In this case, the product types are changed without resetting the other filters or sorting.
   * Example: In the case of keyboard/mouse/combo types (when inside of "Keyboards and Mice" group).
   */
  const handleProductTypeInnerChange = (productType: ProductTypeName[]) => {
    setSelectedProductTypes(productType);

    // only reset pagination (keep other filters and sorting)
    setSelectedPage(0);
  };

  const handleDynamicFiltersSelection = (
    dynamicFiltersValues: DynamicFilters
  ) => {
    setDynamicFiltersSelectedValues(dynamicFiltersValues);

    // only reset pagination
    setSelectedPage(0);
  };

  const handleSortingSelection = (sorting: SortingParams) => {
    setSelectedSorting({
      orderBy: sorting.orderBy,
      sortOrder: sorting.sortOrder,
    });

    // only reset pagination
    setSelectedPage(0);
  };

  const handlePageChange = (newPage: number) => {
    // only update the page (keep all filters and sorting)
    setSelectedPage(newPage);
  };

  // handling Shopping Country changes
  useEffect(() => {
    // only reset pagination (the carts will cleared by the ShoppingCountrySelector component)
    setSelectedPage(0);
  }, [selectedShoppingCountry]);

  // Applying filters/sorting/pagination selections
  useEffect(() => {
    applyFiltersSelections({
      selectedProductTypes,
      dynamicFiltersSelectedValues,
      selectedSorting,
      selectedPage,
      needsWindowsZtpAvailable,
      countryCode:
        selectedShoppingCountry.value === 'other' // just for typescript (this case will never happen here)
          ? undefined
          : selectedShoppingCountry.value,
      specificProductOriginName: specificProductOriginName,
    });
  }, [
    applyFiltersSelections,
    selectedProductTypes,
    dynamicFiltersSelectedValues,
    selectedSorting,
    selectedPage,
    selectedProductTypeGroup,
    needsWindowsZtpAvailable,
    selectedShoppingCountry,
    specificProductOriginName,
  ]);

  const [
    getProduct,
    { isFetching: isLoadingProductDetails, isError: hasProductDetailsError },
  ] = useLazyGetProductQuery();

  const getProductDetails = useCallback(
    async (itemId: string) => {
      const product = await getProduct(itemId, true).unwrap();
      return product;
    },
    [getProduct]
  );

  useItemDetailsModal({
    items: productList,
    queryParam: SelectionParamKeys.ProductId,
    store: Store.Catalog,
    loadingItemsList: isProductListLoading,
    loadingItemDetails: isLoadingProductDetails,
    onAddItem: item => addCatalogCartEntry(item, true),
    onRemoveItem: removeCatalogCartEntry,
    hasItemDetailsError: hasProductDetailsError,
    itemDetailsErrorMessage: t('storefront.catalog.productDetailsError'),
    onItemDetailsAsyncRequest: getProductDetails,
  });

  return (
    <Flex
      vertical
      rowGap={spaces.space_xl}
      width="100%"
      data-testid="catalog-store">
      <ProductTypeGroupSelector
        selectedProductTypeGroup={selectedProductTypeGroup}
        onProductTypeGroupSelect={onProductTypeGroupSelect}
        onProductTypesSelectFromGroup={handleTypesSelectionFromGroup}
        laptopRecommendationUrl={laptopRecommendationUrl}
      />
      {/* BuyItAgain suggestions */}
      {storefrontBuyItAgain && isEmployeeSelected && (
        <BuyItAgainSuggestionBox
          selectedEmployeeId={selectedEmployee?.value ?? ''}
          selectedProductTypes={selectedProductTypes}
          needsWindowsZtpAvailable={needsWindowsZtpAvailable}
          selectedCountryCode={selectedShoppingCountry.value}
          specificProductOriginName={specificProductOriginName}
          selectedItemsIds={catalogCart.map(entry => entry.item.id)}
          onAddItem={item => addCatalogCartEntry(item, true)}
          onRemoveItem={removeCatalogCartEntry}
        />
      )}

      {/* Catalog products (filters + list) */}
      {!hasProductListError && (
        <Flex vertical width="100%" rowGap={spaces.space_m}>
          <DynamicFiltersGroup
            isAvailableDynamicFiltersLoading={isAvailableDynamicFiltersLoading}
            availableDynamicFilters={availableDynamicFilters}
            dynamicFiltersSelectedValues={dynamicFiltersSelectedValues}
            onDynamicFiltersValuesSelect={handleDynamicFiltersSelection}
            selectedProductTypeGroup={selectedProductTypeGroup}
            onProductTypesSelect={handleProductTypeInnerChange}
          />
          <Box alignSelf={md ? 'flex-end' : 'flex-start'}>
            <SortingSelector
              width="18rem"
              selectedSorting={selectedSorting}
              onSortingSelect={handleSortingSelection}
            />
          </Box>
        </Flex>
      )}
      <ItemList
        id="catalog-list"
        store={Store.Catalog}
        items={productList}
        onAddItem={item => addCatalogCartEntry(item, true)}
        onRemoveItem={removeCatalogCartEntry}
        totalItemsResult={productsResponse?.paginatedResponse.total ?? 0}
        selectedPage={selectedPage}
        onPageChange={handlePageChange}
        isLoading={isProductListLoading}
        hasError={hasProductListError}
        errorMessage={t('storefront.catalog.productListError')}
        selectedItemsIds={catalogCart.map(entry => entry.item.id)}
      />
    </Flex>
  );
};
